import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { loginSuccess, setLoading, setError } from '../store/slices/authSlice';
import { RootState } from '../store/store';
import { showCustomToast } from '../components/common/CustomToast';
import SEO from '../components/common/SEO';
import RegisterIllustration from '../assets/register.png';
import './RegisterPage.css';

const RegisterPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoading, error } = useSelector((state: RootState) => state.auth);
  
  const [referralCode, setReferralCode] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [validationErrors, setValidationErrors] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Check for referral code in URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
    }
  }, [searchParams]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateUsername = (username: string): boolean => {
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    return usernameRegex.test(username);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Real-time validation
    if (name === 'username') {
      if (value.length > 0 && value.length < 3) {
        setValidationErrors(prev => ({ ...prev, username: 'Username must be at least 3 characters' }));
      } else if (value.length > 20) {
        setValidationErrors(prev => ({ ...prev, username: 'Username must be at most 20 characters' }));
      } else if (value && !validateUsername(value)) {
        setValidationErrors(prev => ({ ...prev, username: 'Username can only contain letters, numbers, and underscores' }));
      } else {
        setValidationErrors(prev => ({ ...prev, username: '' }));
      }
    }

    if (name === 'email') {
      if (value && !validateEmail(value)) {
        setValidationErrors(prev => ({ ...prev, email: 'Invalid email format' }));
      } else {
        setValidationErrors(prev => ({ ...prev, email: '' }));
      }
    }

    if (name === 'password') {
      if (value.length > 0 && value.length < 8) {
        setValidationErrors(prev => ({ ...prev, password: 'Password must be at least 8 characters' }));
      } else {
        setValidationErrors(prev => ({ ...prev, password: '' }));
      }

      // Also check confirm password if it's filled
      if (formData.confirmPassword && value !== formData.confirmPassword) {
        setValidationErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
      } else if (formData.confirmPassword) {
        setValidationErrors(prev => ({ ...prev, confirmPassword: '' }));
      }
    }

    if (name === 'confirmPassword') {
      if (value && value !== formData.password) {
        setValidationErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
      } else {
        setValidationErrors(prev => ({ ...prev, confirmPassword: '' }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(setError(null));

    // Validate before submit
    if (!validateUsername(formData.username)) {
      dispatch(setError('Please enter a valid username (3-20 characters, letters, numbers, underscores only)'));
      return;
    }

    if (!validateEmail(formData.email)) {
      dispatch(setError('Please enter a valid email address'));
      return;
    }

    if (formData.password.length < 8) {
      dispatch(setError('Password must be at least 8 characters'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      dispatch(setError('Passwords do not match'));
      return;
    }

    dispatch(setLoading(true));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          referralCode: referralCode || undefined
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      dispatch(loginSuccess({
        user: data.user,
        token: data.token,
      }));

      showCustomToast.success('Account created successfully! Welcome to SolBlox.', 4000);

      navigate('/');

    } catch (err: any) {
      console.error('Signup error:', err);
      const errorMessage = err.message || 'Registration failed';
      dispatch(setError(errorMessage));
      showCustomToast.error(errorMessage, 4000);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const hasValidationErrors = Object.values(validationErrors).some(err => err !== '');
  const allFieldsFilled = formData.username && formData.email && formData.password && formData.confirmPassword;
  const canSubmit = allFieldsFilled && !hasValidationErrors && !isLoading;

  return (
    <>
      <SEO 
        title="Sign Up - SolBlox | Flip. Spin. Win."
        description="Create your account on SolBlox - the #1 solana based coinflipping site. Join now and start winning!"
        url="https://solblox.com/register"
      />
      <div className="auth-page">
      <div className="auth-container register-container">
        <div className="auth-illustration">
          <img src={RegisterIllustration} alt="Sign Up" />
        </div>
        <div className="auth-content">
          <div className="auth-header">
            <h1>Create Account</h1>
            <p>Sign up to start playing</p>
            {referralCode && (
              <div style={{ 
                marginTop: '12px', 
                padding: '10px 16px', 
                background: 'rgba(34, 197, 94, 0.1)', 
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '8px',
                color: '#22c55e',
                fontSize: '0.9rem',
                fontWeight: '600'
              }}>
                🎉 Using referral code: <strong>{referralCode}</strong>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <div className="input-wrapper">
              <svg className="input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                minLength={3}
                maxLength={20}
                placeholder="Username"
                disabled={isLoading}
                className={validationErrors.username ? 'error' : ''}
              />
            </div>
            {validationErrors.username && <span className="validation-error">{validationErrors.username}</span>}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <svg className="input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Email"
                disabled={isLoading}
                className={validationErrors.email ? 'error' : ''}
              />
            </div>
            {validationErrors.email && <span className="validation-error">{validationErrors.email}</span>}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <svg className="input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                minLength={8}
                placeholder="Password"
                disabled={isLoading}
                className={validationErrors.password ? 'error' : ''}
              />
            </div>
            {validationErrors.password && <span className="validation-error">{validationErrors.password}</span>}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <svg className="input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                minLength={8}
                placeholder="Confirm Password"
                disabled={isLoading}
                className={validationErrors.confirmPassword ? 'error' : ''}
              />
            </div>
            {validationErrors.confirmPassword && <span className="validation-error">{validationErrors.confirmPassword}</span>}
          </div>

          {error && <div className="error-message">{error}</div>}

          <button 
            type="submit" 
            className="place-bet-btn auth-btn" 
            disabled={!canSubmit}
          >
            {isLoading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        <div className="auth-switch">
          <p>
            Already have an account? <Link to="/login" className="switch-link">Login</Link>
          </p>
        </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default RegisterPage;
